import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PURCHASE_URL = `${API_URL}/purchase-orders`;
const LOV_URL = `${API_URL}/commonLOVs`;
const PURCHASE_ORDER_LINE_URL = `${API_URL}/purchase-order-lines`;

// Get auth token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const purchaseOrderService = {
  // Get all purchase orders with filters
  getAllOrders: async (filters = {}) => {
    try {
      const response = await axios.get(PURCHASE_URL, {
        headers: getAuthHeader(),
        params: filters
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch purchase orders' };
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      const response = await axios.get(`${PURCHASE_URL}/byId/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order' };
    }
  },

  // Create new order
  createOrder: async (orderData) => {
    try {
      const response = await axios.post(PURCHASE_URL, orderData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create order' };
    }
  },

  // Update order
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await axios.put(`${PURCHASE_URL}/${orderId}`, orderData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update order' };
    }
  },

  // Delete order
  deleteOrder: async (orderId) => {
    try {
      const response = await axios.delete(`${PURCHASE_URL}/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete order' };
    }
  },

  // Get all vendors
  getVendors: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/lhvendors`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch vendors' };
    }
  },

  // Get items by subsidiary
  getItems: async (subsidiary) => {
    try {
      const response = await axios.get(`${LOV_URL}/lhitems/${subsidiary}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch items' };
    }
  },

  // Get payment types
  getPaymentTypes: async () => {
    try {
      const response = await axios.get(`${LOV_URL}/payment-types`, {
        headers: getAuthHeader(),
        params: { from_to: 'F' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch payment types' };
    }
  },

  // Get order lines by order ID
  getOrderLines: async (orderId) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDER_LINE_URL}/byOrderId/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch order lines' };
    }
  },

  // Create order line
  createOrderLine: async (lineData) => {
    try {
      const response = await axios.post(PURCHASE_ORDER_LINE_URL, lineData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create order line' };
    }
  },

  // Update order line
  updateOrderLine: async (lineId, lineData) => {
    try {
      const response = await axios.put(`${PURCHASE_ORDER_LINE_URL}/${lineId}`, lineData, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update order line' };
    }
  },

  // Delete order line
  deleteOrderLine: async (lineId, orderId) => {
    try {
      const response = await axios.delete(`${PURCHASE_ORDER_LINE_URL}/${lineId}/${orderId}`, {
        headers: getAuthHeader()
      });
      return { success: true, data: response.data };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete order line' };
    }
  }
};

export default purchaseOrderService;
